import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import {
  RefreshCw, AlertTriangle, CheckCircle2, XCircle, MinusCircle, Car, MapPin,
  Globe, Layers, Clock, Database, Info, TrendingUp, Ban, Plus, Receipt
} from 'lucide-react';

/**
 * Europcar · GA4 Ölçüm Panosu — canlı GA4, warehouse'a hiç dokunmaz.
 *
 * Backend: GET /api/europcar-ga4/overview?days=28|90|180
 *
 * Sayfanın tasarım kararı: ölçülemeyen adımlar GİZLENMEZ. Huni grafiğinde
 * "ölçülmüyor" bandı, event envanterinde "ölü"/"yok" rozetleri ve ölçüm sağlığı
 * bölümü, elde olan veriyle aynı ağırlıkta gösterilir. Sayfanın işi sadece
 * performansı raporlamak değil, ölçüm boşluğunu görünür kılmak.
 *
 * Sayfa oturumundan türetilen huni adımları "vekil" olarak etiketlidir — gerçek
 * huni event'i değildir. Üstelik bu property'de sayfa görüntüleme oturumdan
 * DÜŞÜK (SPA rota değişimi ölçülmüyor), yani vekil adımlar bile eksik sayar.
 *
 * Sadece Europcar brand'inde görünür (App.js allowBrandIds + Sidebar
 * restrictedBrands). Bilerek yalnız açık tema.
 */

const API_BASE = (() => {
  const raw = (process.env.REACT_APP_API_URL || 'https://api.glowytics.com').trim();
  return raw.replace(/\/+$/, '').replace(/\/api$/, '');
})();

const nf = (n) => Number(n || 0).toLocaleString('tr-TR');
const nf1 = (n) => Number(n || 0).toLocaleString('tr-TR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const pct = (x, d = 1) => `%${(Number(x || 0) * 100).toFixed(d)}`;
const tl = (n) => `${Number(Math.round(n || 0)).toLocaleString('tr-TR')} TL`;
const tlShort = (n) => {
  const v = Number(n || 0);
  if (Math.abs(v) >= 1e6) return `${(v / 1e6).toLocaleString('tr-TR', { maximumFractionDigits: 1 })}M TL`;
  if (Math.abs(v) >= 1e3) return `${(v / 1e3).toLocaleString('tr-TR', { maximumFractionDigits: 0 })}B TL`;
  return `${Math.round(v)} TL`;
};
const ymLabel = (ym) => {
  if (!ym || ym.length !== 6) return ym;
  const ay = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];
  return `${ay[Number(ym.slice(4)) - 1]} ${ym.slice(2, 4)}`;
};
const RETENTION_TR = {
  TWO_MONTHS: '2 ay', FOURTEEN_MONTHS: '14 ay', TWENTY_SIX_MONTHS: '26 ay',
  THIRTY_EIGHT_MONTHS: '38 ay', FIFTY_MONTHS: '50 ay'
};

/* ---------------------------------------------------------------- parçacıklar */

function Kpi({ label, value, sub, tone = 'slate' }) {
  const tones = {
    slate: 'text-slate-900', emerald: 'text-emerald-700', amber: 'text-amber-700', rose: 'text-rose-700'
  };
  return (
    <div className="border border-slate-200 bg-white px-4 py-3">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`mt-1 text-[24px] font-bold leading-none tabular-nums ${tones[tone]}`}>{value}</div>
      {sub ? <div className="mt-1 text-[12px] leading-snug text-slate-500">{sub}</div> : null}
    </div>
  );
}

function Section({ icon: Icon, eyebrow, title, children, note }) {
  return (
    <section className="mt-10">
      <div className="flex items-start gap-2.5">
        {Icon ? <Icon size={18} className="mt-[3px] shrink-0 text-emerald-700" /> : null}
        <div>
          <h2 className="text-[12px] font-bold uppercase tracking-wider text-emerald-700">{eyebrow}</h2>
          <p className="mt-1 text-[20px] font-bold leading-snug tracking-tight text-slate-900">{title}</p>
          {note ? <p className="mt-1.5 max-w-[86ch] text-[13px] leading-relaxed text-slate-500">{note}</p> : null}
        </div>
      </div>
      <div className="mt-4">{children}</div>
    </section>
  );
}

/** Satır içi oransal çubuk — tablo hücrelerinde büyüklük hissi için. */
function Bar({ value, max, tone = 'emerald' }) {
  const w = max > 0 ? Math.max(1, (value / max) * 100) : 0;
  const tones = { emerald: 'bg-emerald-500', slate: 'bg-slate-400', amber: 'bg-amber-500', rose: 'bg-rose-500' };
  return (
    <div className="h-1.5 w-full bg-slate-100">
      <div className={`h-1.5 ${tones[tone]}`} style={{ width: `${w}%` }} />
    </div>
  );
}

function Badge({ kind, children }) {
  const map = {
    var: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    olu: 'bg-rose-50 text-rose-700 border-rose-200',
    yok: 'bg-slate-100 text-slate-600 border-slate-200',
    uyari: 'bg-amber-50 text-amber-800 border-amber-200'
  };
  return (
    <span className={`inline-flex items-center border px-1.5 py-0.5 text-[11px] font-semibold ${map[kind] || map.yok}`}>
      {children}
    </span>
  );
}

function Th({ children, right }) {
  return (
    <th className={`border-b border-slate-200 px-3 py-2 text-[11px] font-bold uppercase tracking-wider text-slate-500 ${right ? 'text-right' : 'text-left'}`}>
      {children}
    </th>
  );
}
function Td({ children, right, mono, className = '' }) {
  return (
    <td className={`border-b border-slate-100 px-3 py-2 text-[13px] text-slate-700 ${right ? 'text-right' : ''} ${mono ? 'tabular-nums' : ''} ${className}`}>
      {children}
    </td>
  );
}

/* ------------------------------------------------------------------- huni */

/**
 * Huni. Ölçülen adımlar dolu çubuk, vekil adımlar taralı, ölçülmeyenler
 * kırmızı kesikli bant olarak çizilir. Ölçülmeyen adımlarda SAYI YOKTUR —
 * boş bırakmak yerine tahmin basmak sayfanın anlamını bozardı.
 */
function Funnel({ steps }) {
  const max = Math.max(...steps.map((s) => s.value || 0), 1);
  const measured = steps.filter((s) => s.value != null);

  return (
    <div className="border border-slate-200 bg-white">
      {steps.map((s, i) => {
        const has = s.value != null;
        const w = has ? Math.max(0.6, (s.value / max) * 100) : 100;
        const prev = has ? [...measured].reverse().find((m) => steps.indexOf(m) < i) : null;
        const drop = prev && prev.value ? s.value / prev.value : null;

        const fill =
          s.kind === 'olculuyor' ? 'bg-emerald-500'
            : s.kind === 'vekil' ? 'bg-emerald-200'
              : s.kind === 'eksik' ? 'bg-amber-300'
                : 'bg-transparent';

        return (
          <div key={s.key} className={`px-4 py-3 ${i ? 'border-t border-slate-100' : ''} ${s.kind === 'olculmuyor' ? 'bg-rose-50/40' : ''}`}>
            <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
              <div className="flex items-center gap-2">
                {s.kind === 'olculuyor' ? <CheckCircle2 size={14} className="text-emerald-600" />
                  : s.kind === 'olculmuyor' ? <XCircle size={14} className="text-rose-500" />
                    : <MinusCircle size={14} className="text-amber-500" />}
                <span className={`text-[14px] font-semibold ${s.kind === 'olculmuyor' ? 'text-rose-800' : 'text-slate-900'}`}>
                  {s.label}
                </span>
                {s.kind === 'vekil' ? <Badge kind="uyari">vekil ölçüm</Badge> : null}
                {s.kind === 'eksik' ? <Badge kind="uyari">eksik veri</Badge> : null}
              </div>
              <div className="flex items-baseline gap-3">
                {drop != null ? (
                  <span className={`text-[12px] tabular-nums ${drop > 1 ? 'text-amber-600' : 'text-slate-400'}`}>{pct(drop, 1)}</span>
                ) : null}
                <span className={`text-[16px] font-bold tabular-nums ${has ? 'text-slate-900' : 'text-rose-700'}`}>
                  {has ? nf(s.value) : 'ÖLÇÜLMÜYOR'}
                </span>
              </div>
            </div>
            <div className="mt-2 h-2 w-full bg-slate-100">
              {has
                ? <div className={`h-2 ${fill}`} style={{ width: `${w}%` }} />
                : <div className="h-2 w-full border border-dashed border-rose-300 bg-rose-100/50" />}
            </div>
            <div className="mt-1.5 text-[12px] text-slate-500">{s.source}</div>
          </div>
        );
      })}
    </div>
  );
}

/* --------------------------------------------------------------- aylık grafik */

/**
 * Aylık ciro + ortalama kiralama süresi. 202512 ayı ayrıca işaretlenir:
 * o ay purchase event'i 9 Ara'dan itibaren hiç gelmedi, yani sütun gerçek
 * düşüş değil ÖLÇÜM BOŞLUĞU. İşaretlenmezse grafik yalan söyler.
 */
function MonthlyChart({ rows }) {
  if (!rows || !rows.length) return null;
  const W = 980, H = 190, PAD_L = 8, PAD_R = 8, PAD_B = 26, PAD_T = 12;
  const maxRev = Math.max(...rows.map((r) => r.revenue), 1);
  const bw = (W - PAD_L - PAD_R) / rows.length;

  const daysRows = rows.filter((r) => r.avgDays != null);
  const maxDays = Math.max(...daysRows.map((r) => r.avgDays), 1);
  const yDays = (v) => PAD_T + (1 - v / maxDays) * (H - PAD_T - PAD_B);
  const xMid = (i) => PAD_L + i * bw + bw / 2;
  const linePts = rows
    .map((r, i) => (r.avgDays != null ? `${xMid(i)},${yDays(r.avgDays)}` : null))
    .filter(Boolean).join(' ');

  return (
    <div className="border border-slate-200 bg-white p-4">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" role="img" aria-label="Aylık ciro ve ortalama kiralama süresi">
        {rows.map((r, i) => {
          const h = (r.revenue / maxRev) * (H - PAD_T - PAD_B);
          const gap = r.ym === '202512';
          return (
            <g key={r.ym}>
              <rect
                x={PAD_L + i * bw + 3} y={H - PAD_B - h} width={Math.max(2, bw - 6)} height={h}
                fill={gap ? '#fecdd3' : '#a7f3d0'} stroke={gap ? '#f43f5e' : 'none'}
                strokeDasharray={gap ? '3 2' : undefined}
              />
              <text x={xMid(i)} y={H - 14} textAnchor="middle" className="fill-slate-400" style={{ fontSize: 10 }}>
                {ymLabel(r.ym)}
              </text>
              {gap ? (
                <text x={xMid(i)} y={H - PAD_B - h - 5} textAnchor="middle" className="fill-rose-600" style={{ fontSize: 9, fontWeight: 700 }}>
                  boşluk
                </text>
              ) : null}
            </g>
          );
        })}
        {linePts ? <polyline points={linePts} fill="none" stroke="#0f766e" strokeWidth="2" /> : null}
        {rows.map((r, i) => (r.avgDays != null
          ? <circle key={`d${r.ym}`} cx={xMid(i)} cy={yDays(r.avgDays)} r="2.5" fill="#0f766e" />
          : null))}
      </svg>
      <div className="mt-2 flex flex-wrap items-center gap-4 text-[12px] text-slate-500">
        <span className="flex items-center gap-1.5"><span className="inline-block h-2.5 w-4 bg-emerald-200" /> aylık ciro</span>
        <span className="flex items-center gap-1.5"><span className="inline-block h-[2px] w-4 bg-teal-700" /> ort. kiralama günü (Oca 26'dan itibaren)</span>
        <span className="flex items-center gap-1.5"><span className="inline-block h-2.5 w-4 border border-dashed border-rose-500 bg-rose-200" /> ölçüm boşluğu</span>
      </div>
    </div>
  );
}

/* --------------------------------------------------- rezervasyon anatomisi */

const DURUM = {
  tam: { etiket: 'VAR', renk: 'text-emerald-700', nokta: 'bg-emerald-500', satir: '' },
  kismi: { etiket: 'KISMİ', renk: 'text-amber-700', nokta: 'bg-amber-400', satir: 'bg-amber-50/50' },
  bos: { etiket: 'HEP BOŞ', renk: 'text-slate-500', nokta: 'bg-slate-300', satir: 'bg-slate-50' },
  kayitli_degil: { etiket: 'GA4\'TE YOK', renk: 'text-rose-700', nokta: 'bg-rose-500', satir: 'bg-rose-50/60' },
  bilinmiyor: { etiket: '—', renk: 'text-slate-400', nokta: 'bg-slate-200', satir: '' }
};

/**
 * Örnek rezervasyon fişi. Gerçek bir purchase satırı; dolu alanlar değerleriyle,
 * ölçülmeyen alanlar kırmızı kesikli boşluk olarak basılır. Bölümün asıl anlatımı
 * bu kart — tablo detayı, kart ise tek bakışta "neyi bilmiyoruz"u gösterir.
 */
function OrderReceipt({ sample, fields }) {
  if (!sample) return null;
  const dolu = [
    ['Rezervasyon no', sample.transactionId],
    ['Alış ofisi', sample.pickup],
    ['İade ofisi', sample.dropoff],
    ['Araç grubu', sample.car],
    ['Kiralama süresi', `${nf(sample.rentalDays)} gün`],
    ['Tutar', tl(sample.revenue)],
    ['Kupon', sample.coupon && sample.coupon !== '(not set)' ? sample.coupon : 'kullanılmamış'],
    ['Kanal', sample.channel],
    ['Ülke', sample.country],
    ['Cihaz', sample.device]
  ];
  // Kırpma YOK: "bilmediklerimiz" sütununun "bildiklerimiz"in iki katı uzunlukta
  // olması bölümün anlatımının bir parçası. Pazarlama bağlamı hariç, çünkü onlar
  // GA4'ün otomatik alanları ve zaten hepsi dolu.
  const eksik = fields
    .filter((f) => f.durum === 'kayitli_degil' || f.durum === 'bos')
    .filter((f) => f.group !== 'Pazarlama bağlamı');

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <div className="border border-emerald-200 bg-white">
        <div className="border-b border-emerald-200 bg-emerald-50 px-4 py-2 text-[12px] font-bold uppercase tracking-wider text-emerald-800">
          Bu rezervasyonda bildiklerimiz
        </div>
        <table className="w-full">
          <tbody>
            {dolu.map(([k, v]) => (
              <tr key={k} className="border-b border-slate-100 last:border-0">
                <td className="w-[42%] px-4 py-2 align-top text-[13px] text-slate-500">{k}</td>
                <td className="px-4 py-2 text-[13px] font-semibold text-slate-900">{v || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="border border-rose-200 bg-white">
        <div className="border-b border-rose-200 bg-rose-50 px-4 py-2 text-[12px] font-bold uppercase tracking-wider text-rose-800">
          Aynı rezervasyonda bilmediklerimiz
        </div>
        <table className="w-full">
          <tbody>
            {eksik.map((f) => (
              <tr key={f.key} className="border-b border-slate-100 last:border-0">
                <td className="w-[42%] px-4 py-2 align-top text-[13px] text-slate-500">{f.label}</td>
                <td className="px-4 py-2">
                  <span className="inline-block w-full border-b-2 border-dashed border-rose-300 pb-0.5 text-[12px] font-semibold uppercase tracking-wider text-rose-600">
                    ölçülmüyor
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------- sayfa */

const RANGES = [{ d: 28, l: '28 gün' }, { d: 90, l: '90 gün' }, { d: 180, l: '180 gün' }];

export default function EuropcarGa4Panel() {
  const brandId = typeof window !== 'undefined' ? localStorage.getItem('currentBrandId') : null;
  const headers = useMemo(
    () => ({ 'Content-Type': 'application/json', ...(brandId ? { 'X-Brand-ID': brandId } : {}) }),
    [brandId]
  );

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState(90);
  const [allStations, setAllStations] = useState(false);
  const [allCars, setAllCars] = useState(false);

  const load = useCallback(async (refresh) => {
    if (!brandId) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/europcar-ga4/overview?days=${days}${refresh ? '&refresh=1' : ''}`, {
        credentials: 'include', headers
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'GA4 verisi yüklenemedi');
      setData(json);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [brandId, headers, days]);

  useEffect(() => { load(false); }, [load]);

  const d = data;
  const s = d?.summary || {};
  const h = d?.health || {};

  const rentalDays = useMemo(
    () => (d?.stations || []).reduce((a, x) => a + x.rentalDays, 0),
    [d]
  );
  const avgDays = s.transactions ? rentalDays / s.transactions : 0;

  const stations = d?.stations || [];
  const stationsShown = allStations ? stations : stations.slice(0, 20);
  const maxStationRev = stations[0] ? Math.max(...stations.map((x) => x.revenue)) : 0;

  const cars = d?.cars || [];
  const carsShown = allCars ? cars : cars.slice(0, 20);
  const maxCarRev = cars.length ? Math.max(...cars.map((x) => x.revenue)) : 0;

  const countries = (d?.countries || []).filter((c) => c.sessions >= 300).slice(0, 14);
  const maxCountry = countries[0]?.sessions || 0;
  const channels = d?.channels || [];
  const maxChannel = channels[0]?.sessions || 0;
  const campaigns = (d?.campaigns || []).filter((c) => c.sessions >= 200).slice(0, 16);
  const locPages = (d?.locationPages || []).slice(0, 16);
  const maxHourTx = Math.max(...(d?.hours || []).map((x) => x.transactions), 1);
  const maxDowTx = Math.max(...(d?.dow || []).map((x) => x.transactions), 1);
  const extraGroups = d?.extraGroups || [];

  const missing = (d?.beklenen || []).filter((b) => b.durum !== 'var');

  return (
    <div lang="tr" className="mx-auto max-w-[1360px] px-4 py-6">

      {/* --------------------------------------------------------- başlık */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-[12px] font-bold uppercase tracking-wider text-emerald-700">Europcar · GA4 Ölçüm Panosu</h1>
          <p className="mt-1.5 text-[26px] font-bold leading-tight tracking-tight text-slate-900">
            Elde ne var, ne yok
          </p>
          <p className="mt-1.5 max-w-[86ch] text-[13px] leading-relaxed text-slate-500">
            Tamamı canlı GA4 Data API. Bu sayfa hiçbir veri ambarına bakmaz — bakamaz da: property'de
            BigQuery export yok ve event verisi saklama süresi {RETENTION_TR[h.retention] || '—'}.
            {d?.meta ? ` Property ${d.meta.propertyId} · ${d.meta.range.start} → ${d.meta.range.end}.` : ''}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex border border-slate-200">
            {RANGES.map((r) => (
              <button
                key={r.d} onClick={() => setDays(r.d)}
                className={`px-3 py-1.5 text-[12px] font-semibold ${days === r.d ? 'bg-emerald-700 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
              >{r.l}</button>
            ))}
          </div>
          <button
            onClick={() => load(true)} disabled={loading}
            className="flex items-center gap-1.5 border border-slate-200 bg-white px-3 py-1.5 text-[12px] font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50"
          >
            <RefreshCw size={13} className={loading ? 'animate-spin' : ''} /> Yenile
          </button>
        </div>
      </div>

      {!d && loading ? <p className="mt-8 text-[13px] text-slate-500">GA4'ten çekiliyor…</p> : null}
      {!d && !loading ? <p className="mt-8 text-[13px] text-slate-500">Veri yok.</p> : null}

      {d ? (
        <>
          {/* GA4 kota hatası vb. — eksik bölüm "sıfır" sanılmasın diye açıkça söylenir. */}
          {d.meta?.failed?.length ? (
            <div className="mt-6 border-l-4 border-amber-500 bg-amber-50 px-4 py-3 text-[13px] leading-relaxed text-amber-900">
              <strong>Bu yüklemede bazı raporlar GA4'ten gelemedi.</strong>{' '}
              {d.meta.failed.map((f) => f.reports.join(', ')).join(' · ')} — ilgili bölümler eksik görünüyor,
              sıfır değil. Yenile'ye basın.
            </div>
          ) : null}

          {/* ------------------------------------------------- ölçüm uyarısı */}
          <div className="mt-6 border-l-4 border-rose-500 bg-rose-50 px-4 py-3">
            <div className="flex items-start gap-2">
              <AlertTriangle size={16} className="mt-0.5 shrink-0 text-rose-600" />
              <div className="text-[13px] leading-relaxed text-rose-900">
                <strong>8 Aralık 2025'te tag'leme kırıldı.</strong> <code className="bg-rose-100 px-1">view_item</code> ve{' '}
                <code className="bg-rose-100 px-1">add_to_cart</code> o gün son kez tetiklendi ve geri gelmedi.{' '}
                <code className="bg-rose-100 px-1">purchase</code> ise 9 Aralık 2025 – 8 Ocak 2026 arası{' '}
                <strong>31 gün boyunca hiç gelmedi</strong> (trafik normal akıyordu).{' '}
                <code className="bg-rose-100 px-1">view_item_list</code> 19 Ocak 2026'da devreye girdi ama{' '}
                <code className="bg-rose-100 px-1">items[]</code> dizisi boş gidiyor.
                <div className="mt-1.5">
                  Sonuç: <strong>Aralık 2025'i kapsayan hiçbir dönem kıyaslaması geçerli değil</strong>, ve 2025 ile 2026
                  huni metrikleri kıyaslanamaz.
                </div>
              </div>
            </div>
          </div>

          {/* ------------------------------------------------------ KPI şerit */}
          <div className="mt-6 grid grid-cols-2 gap-px bg-slate-200 md:grid-cols-4 xl:grid-cols-7">
            <Kpi label="Oturum" value={nf(s.sessions)} sub={`${pct(s.newShare)} yeni kullanıcı`} />
            <Kpi label="Rezervasyon" value={nf(s.transactions)} sub={`${pct(s.cvr, 2)} dönüşüm`} tone="emerald" />
            <Kpi label="Ciro" value={tlShort(s.revenue)} sub={`${tl(s.aov)} ortalama sepet`} tone="emerald" />
            <Kpi label="Ort. kiralama" value={`${nf1(avgDays)} gün`} sub={`${nf(rentalDays)} toplam gün`} />
            <Kpi label="Ekstra cirosu" value={tlShort(d.extrasRevenue)} sub={`cironun ${pct(d.extrasRevenue / (s.revenue || 1))}'i`} />
            <Kpi label="Tek yön payı" value={pct(d.oneWay.share)} sub={`${nf(d.oneWay.diff)} farklı istasyona bırakış`} />
            <Kpi label="Yurt dışı ciro" value={pct(d.abroad.revenueShare)} sub={`oturumun ${pct(d.abroad.sessionShare)}'i · ${tl(d.abroad.aov)} sepet`} tone="amber" />
          </div>

          {/* ---------------------------------------------------------- huni */}
          <Section
            icon={Layers} eyebrow="Huni" title="Rezervasyona giden yol — ve ölçülmeyen halkalar"
            note="Yeşil dolu = gerçek event. Açık yeşil = sayfa oturumundan türetilen vekil ölçüm. Kırmızı kesikli = hiç ölçülmüyor; buraya tahmin basılmadı. Vekil adımlar birbirinin içinden geçmez — her biri o sayfayı gören oturum sayısıdır, bu yüzden sonraki adım öncekini aşabilir. Sağdaki oran da bu yüzden %100'ü geçebilir; bu bir hata değil, elimizdekinin gerçek huni olmadığının kanıtıdır."
          >
            <Funnel steps={d.funnel} />
          </Section>

          {/* ------------------------------------------------ event envanteri */}
          <Section
            icon={Database} eyebrow="Event envanteri"
            title={`Property'de ${d.events.length} farklı event var`}
            note="Araç kiralama hunisinin olması gereken 16 event'e karşı canlı durum. 'Ölü' = 18 aylık pencerede vardı, seçili aralıkta hiç gelmiyor."
          >
            <div className="grid gap-4 lg:grid-cols-2">
              <div className="border border-slate-200 bg-white">
                <div className="border-b border-slate-200 px-3 py-2 text-[11px] font-bold uppercase tracking-wider text-slate-500">
                  Canlı event'ler
                </div>
                <table className="w-full">
                  <tbody>
                    {d.events.map((e) => (
                      <tr key={e.name}>
                        <Td><code className="text-[12px] text-slate-800">{e.name}</code></Td>
                        <Td right mono>{nf(e.count)}</Td>
                        <Td right mono className="text-slate-400">{nf(e.users)} kullanıcı</Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {d.deadEvents.length ? (
                  <>
                    <div className="border-y border-slate-200 bg-rose-50 px-3 py-2 text-[11px] font-bold uppercase tracking-wider text-rose-700">
                      Ölmüş event'ler
                    </div>
                    <table className="w-full">
                      <tbody>
                        {d.deadEvents.map((e) => (
                          <tr key={e.name}>
                            <Td><code className="text-[12px] text-slate-800">{e.name}</code></Td>
                            <Td right mono className="text-slate-400">{nf(e.total18m)} (18 ay)</Td>
                            <Td right><Badge kind="olu">son: {ymLabel(e.lastMonth)}</Badge></Td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                ) : null}
              </div>

              <div className="border border-slate-200 bg-white">
                <div className="border-b border-slate-200 px-3 py-2 text-[11px] font-bold uppercase tracking-wider text-slate-500">
                  Araç kiralama için beklenen event'ler
                </div>
                <table className="w-full">
                  <tbody>
                    {d.beklenen.map((b) => (
                      <tr key={b.name}>
                        <Td>
                          <div className="flex items-center gap-2">
                            <Badge kind={b.durum}>{b.durum === 'var' ? 'var' : b.durum === 'olu' ? 'ölü' : 'yok'}</Badge>
                            <code className="text-[12px] text-slate-800">{b.name}</code>
                          </div>
                          <div className="mt-0.5 pl-1 text-[12px] leading-snug text-slate-500">{b.why}</div>
                        </Td>
                        <Td right mono className="align-top">{b.count ? nf(b.count) : '—'}</Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </Section>

          {/* -------------------------------------------------- ölçüm sağlığı */}
          <Section
            icon={Info} eyebrow="Ölçüm sağlığı" title="Ayarlar — kod gerektirmeyen sorunlar"
            note="Bu bölümdeki maddelerin hiçbiri site geliştirmesi gerektirmiyor; hepsi GA4 arayüzünden değiştirilebilir."
          >
            <div className="grid gap-4 lg:grid-cols-3">
              <div className="border border-slate-200 bg-white p-4">
                <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Veri saklama & export</div>
                <ul className="mt-2 space-y-2 text-[13px] text-slate-700">
                  <li className="flex items-start gap-2">
                    {h.retention === 'TWO_MONTHS' ? <XCircle size={14} className="mt-0.5 shrink-0 text-rose-500" /> : <CheckCircle2 size={14} className="mt-0.5 shrink-0 text-emerald-600" />}
                    <span>Event verisi saklama: <strong>{RETENTION_TR[h.retention] || h.retention}</strong>
                      {h.retention === 'TWO_MONTHS' ? <span className="text-rose-700"> — 14 ay olmalı. Keşif raporlarında {RETENTION_TR[h.retention]} öncesine gidilemiyor.</span> : null}
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 size={14} className="mt-0.5 shrink-0 text-emerald-600" />
                    <span>Kullanıcı verisi: <strong>{RETENTION_TR[h.userRetention] || h.userRetention}</strong></span>
                  </li>
                  <li className="flex items-start gap-2">
                    {h.bigQueryLinked ? <CheckCircle2 size={14} className="mt-0.5 shrink-0 text-emerald-600" /> : <XCircle size={14} className="mt-0.5 shrink-0 text-rose-500" />}
                    <span>BigQuery export: <strong>{h.bigQueryLinked ? 'bağlı' : 'yok'}</strong>
                      {!h.bigQueryLinked ? <span className="text-rose-700"> — ham event verisi hiçbir yerde yedeklenmiyor. Saklama süresi dolan veri kalıcı kayıp.</span> : null}
                    </span>
                  </li>
                </ul>
              </div>

              <div className="border border-slate-200 bg-white p-4">
                <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                  Gelişmiş ölçüm — ana stream
                </div>
                <div className="mt-2 space-y-1.5">
                  {(h.enhancedMeasurement || []).map((f) => (
                    <div key={f.key} className="flex items-center gap-2 text-[13px]">
                      {f.on ? <CheckCircle2 size={13} className="shrink-0 text-emerald-600" /> : <XCircle size={13} className="shrink-0 text-rose-500" />}
                      <span className={f.on ? 'text-slate-700' : 'text-rose-800'}>{f.label}</span>
                    </div>
                  ))}
                </div>
                {h.enhancedMeasurementOff ? (
                  <p className="mt-2.5 text-[12px] leading-relaxed text-rose-700">
                    {h.enhancedMeasurementOff} özelliğin tamamı kapalı. Özellikle <strong>SPA sayfa değişimi</strong>:
                    rezervasyon akışı tek sayfa uygulaması olarak dönüyor, adım geçişleri sayfa görüntüleme üretmiyor.
                    Kanıt: sayfa/oturum {nf1(s.pvPerSession)} ve rezervasyon adımlarında oturum sayısı görüntülemeyi aşıyor.
                  </p>
                ) : null}
              </div>

              <div className="border border-slate-200 bg-white p-4">
                <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Anahtar event'ler</div>
                <div className="mt-2 space-y-1.5">
                  {(h.keyEvents || []).map((k) => (
                    <div key={k.name} className="flex items-center justify-between gap-2 text-[13px]">
                      <span className="flex items-center gap-2 truncate">
                        {k.durum === 'var' ? <CheckCircle2 size={13} className="shrink-0 text-emerald-600" /> : <XCircle size={13} className="shrink-0 text-rose-500" />}
                        <code className={`truncate text-[12px] ${k.durum === 'var' ? 'text-slate-700' : 'text-rose-800'}`}>{k.name}</code>
                      </span>
                      <span className="shrink-0 tabular-nums text-slate-400">{k.count ? nf(k.count) : (k.lastMonth ? `son ${ymLabel(k.lastMonth)}` : 'hiç')}</span>
                    </div>
                  ))}
                </div>
                {h.keyEventsDead ? (
                  <p className="mt-2.5 text-[12px] leading-relaxed text-rose-700">
                    {h.keyEventsDead} anahtar event ölü. Google Ads hesabı bu property'ye bağlı — ölü dönüşümlerin
                    içe aktarma listesinde durup durmadığı kontrol edilmeli.
                  </p>
                ) : null}
              </div>
            </div>

            <div className="mt-4 grid gap-4 lg:grid-cols-2">
              <div className="border border-slate-200 bg-white p-4">
                <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                  Özel boyutlar — {h.customDimensionSlots?.used}/{h.customDimensionSlots?.limit} event · {h.itemDimensionSlots?.used}/{h.itemDimensionSlots?.limit} item
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {(h.customDimensions || []).map((c) => {
                    const bos = c.parameter === 'event_category' || c.parameter === 'event_label';
                    return (
                      <span key={c.parameter} className={`border px-1.5 py-0.5 text-[12px] ${bos ? 'border-rose-200 bg-rose-50 text-rose-700' : 'border-emerald-200 bg-emerald-50 text-emerald-700'}`}>
                        {c.parameter}{bos ? ' · boş' : ''}
                      </span>
                    );
                  })}
                  {(h.customMetrics || []).map((c) => (
                    <span key={c.parameter} className="border border-teal-200 bg-teal-50 px-1.5 py-0.5 text-[12px] text-teal-700">
                      {c.parameter} · metrik
                    </span>
                  ))}
                </div>
                <p className="mt-2.5 text-[12px] leading-relaxed text-slate-500">
                  <code>event_category</code> ve <code>event_label</code> %100 boş — hiç doldurulmamış. Item kapsamlı
                  boyut hiç kullanılmamış ({h.itemDimensionSlots?.limit} slot boş), yani araç grubu bazlı analiz
                  event slotu harcamadan yapılabilir.
                </p>
              </div>

              <div className="border border-slate-200 bg-white p-4">
                <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Veri akışları</div>
                <div className="mt-2 space-y-2">
                  {(h.streams || []).map((st) => {
                    const olu = /landing/i.test(st.name || '');
                    return (
                      <div key={st.measurementId} className="flex items-start gap-2 text-[13px]">
                        {olu ? <XCircle size={14} className="mt-0.5 shrink-0 text-rose-500" /> : <CheckCircle2 size={14} className="mt-0.5 shrink-0 text-emerald-600" />}
                        <div>
                          <div className={olu ? 'text-rose-800' : 'text-slate-700'}>{st.name}</div>
                          <div className="text-[12px] text-slate-400">{st.measurementId} · {st.uri}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <p className="mt-2.5 text-[12px] leading-relaxed text-rose-700">
                  <strong>landing.europcar.com.tr</strong> akışına seçili aralıkta hiç veri gelmiyor. Kurumsal lead
                  formu anahtar event'leri bu akış için tanımlanmış — kurumsal talep hiç ölçülmüyor.
                </p>
              </div>
            </div>
          </Section>

          {/* ---------------------------------------------------- istasyonlar */}
          <Section
            icon={MapPin} eyebrow="İstasyonlar" title={`${nf(stations.length)} alış istasyonu`}
            note={`pickup_city özel boyutundan. Ortalama kiralama süresi rental_days özel metriğinden. Dış hat terminalleri iç hatlara göre 2 kata yakın uzun kiralama üretiyor — aynı havalimanının iki terminali farklı iki iş kolu.`}
          >
            <div className="overflow-x-auto border border-slate-200 bg-white">
              <table className="w-full min-w-[860px]">
                <thead>
                  <tr>
                    <Th>İstasyon</Th><Th right>Rezervasyon</Th><Th right>Pay</Th>
                    <Th right>Ort. gün</Th><Th right>Günlük ciro</Th><Th right>Sepet</Th><Th right>Ciro</Th>
                  </tr>
                </thead>
                <tbody>
                  {stationsShown.map((st) => (
                    <tr key={st.name} className="hover:bg-slate-50">
                      <Td className="font-medium text-slate-900">{st.name}</Td>
                      <Td right mono>{nf(st.bookings)}</Td>
                      <Td right mono className="text-slate-400">{pct(st.bookings / (d.stationsTotal || 1))}</Td>
                      <Td right mono className={st.avgDays >= 7 ? 'font-bold text-emerald-700' : ''}>{nf1(st.avgDays)}</Td>
                      <Td right mono>{tl(st.dailyRate)}</Td>
                      <Td right mono>{tl(st.aov)}</Td>
                      <Td right mono>
                        <div>{tlShort(st.revenue)}</div>
                        <div className="mt-1"><Bar value={st.revenue} max={maxStationRev} /></div>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {stations.length > 20 ? (
              <button onClick={() => setAllStations((v) => !v)} className="mt-2 text-[12px] font-semibold text-emerald-700 hover:underline">
                {allStations ? 'İlk 20 istasyonu göster' : `Tümünü göster (${stations.length})`}
              </button>
            ) : null}

            <div className="mt-4 grid gap-4 lg:grid-cols-2">
              <div className="border border-slate-200 bg-white p-4">
                <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Tek yön kiralama</div>
                <p className="mt-1.5 text-[13px] leading-relaxed text-slate-600">
                  Alış ve bırakış istasyonu farklı olan rezervasyon payı <strong>{pct(d.oneWay.share)}</strong> ({nf(d.oneWay.diff)} rezervasyon).
                  Bu oran, "Tek Yön Tarifesi Ücreti" kaleminin bağlanma oranıyla birebir tutuyor — iki bağımsız kaynak
                  aynı sonucu veriyor, veri güvenilir.
                </p>
                <table className="mt-3 w-full">
                  <tbody>
                    {d.oneWay.routes.map((r) => (
                      <tr key={`${r.from}->${r.to}`}>
                        <Td className="text-[12px]">{r.from} <span className="text-slate-400">→</span> {r.to}</Td>
                        <Td right mono>{nf(r.bookings)}</Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="border border-slate-200 bg-white p-4">
                <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Lokasyon sayfaları — organik giriş</div>
                <p className="mt-1.5 text-[13px] leading-relaxed text-slate-600">
                  Şehir sayfaları trafik topluyor, rezervasyona çevirmiyor. İstasyon sayfaları belirgin şekilde
                  daha iyi dönüşüyor — içerik önceliği buna göre kurulmalı.
                </p>
                <div className="mt-3 max-h-[320px] overflow-y-auto">
                  <table className="w-full">
                    <thead><tr><Th>Giriş sayfası</Th><Th right>Oturum</Th><Th right>Rez.</Th><Th right>CVR</Th></tr></thead>
                    <tbody>
                      {locPages.map((p) => (
                        <tr key={p.name}>
                          <Td className="text-[12px]">{p.name}</Td>
                          <Td right mono>{nf(p.sessions)}</Td>
                          <Td right mono>{nf(p.transactions)}</Td>
                          <Td right mono className={p.cvr < 0.002 ? 'font-bold text-rose-600' : 'text-slate-500'}>{pct(p.cvr, 2)}</Td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </Section>

          {/* ------------------------------------------------------ araç grubu */}
          <Section
            icon={Car} eyebrow="Filo" title={`${nf(cars.length)} araç grubu`}
            note="car_type özel boyutundan; ACRISS kodları veride mevcut. Günlük ciro = grup cirosu ÷ toplam kiralama günü."
          >
            <div className="overflow-x-auto border border-slate-200 bg-white">
              <table className="w-full min-w-[820px]">
                <thead>
                  <tr>
                    <Th>ACRISS</Th><Th>Araç grubu</Th><Th right>Rezervasyon</Th><Th right>Pay</Th>
                    <Th right>Ort. gün</Th><Th right>Günlük ciro</Th><Th right>Ciro</Th>
                  </tr>
                </thead>
                <tbody>
                  {carsShown.map((c) => (
                    <tr key={c.name + c.acriss} className="hover:bg-slate-50">
                      <Td><code className="text-[12px] font-semibold text-slate-800">{c.acriss || '—'}</code></Td>
                      <Td className="font-medium text-slate-900">{c.name}</Td>
                      <Td right mono>{nf(c.bookings)}</Td>
                      <Td right mono className={c.bookings / (d.carsBookings || 1) > 0.2 ? 'font-bold text-amber-700' : 'text-slate-400'}>
                        {pct(c.bookings / (d.carsBookings || 1))}
                      </Td>
                      <Td right mono>{nf1(c.avgDays)}</Td>
                      <Td right mono>{tl(c.dailyRate)}</Td>
                      <Td right mono>
                        <div>{tlShort(c.revenue)}</div>
                        <div className="mt-1"><Bar value={c.revenue} max={maxCarRev} /></div>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {cars.length > 20 ? (
              <button onClick={() => setAllCars((v) => !v)} className="mt-2 text-[12px] font-semibold text-emerald-700 hover:underline">
                {allCars ? 'İlk 20 grubu göster' : `Tümünü göster (${cars.length})`}
              </button>
            ) : null}
            {cars[0] ? (
              <p className="mt-3 border-l-4 border-amber-400 bg-amber-50 px-3 py-2 text-[13px] leading-relaxed text-amber-900">
                <strong>{cars[0].name}</strong> tek başına rezervasyonun {pct(cars[0].bookings / (d.carsBookings || 1))}'ini,
                cironun {pct(cars[0].revenue / (d.carsRevenue || 1))}'ini taşıyor. Tek bir araç grubunun müsaitliği
                işin beşte birini belirliyor — ve müsaitlik ölçülmüyor.
              </p>
            ) : null}
          </Section>

          {/* ---------------------------------------------------------- ekstra */}
          <Section
            icon={Plus} eyebrow="Ekstralar" title="Koruma ve ek ürün bağlanma oranları"
            note="TR ve EN item adları birleştirildi. Koruma paketi sayısı rezervasyon sayısıyla birebir örtüşüyor — her rezervasyonda tam bir paket var, veri tutarlı."
          >
            <div className="border border-slate-200 bg-white">
              <table className="w-full">
                <thead>
                  <tr><Th>Ürün</Th><Th right>Adet</Th><Th right>Bağlanma</Th><Th right>Birim</Th><Th right>Ciro</Th><Th right>Ciro payı</Th></tr>
                </thead>
                <tbody>
                  {extraGroups.map((g) => (
                    <tr key={g.key} className="hover:bg-slate-50">
                      <Td className="font-medium text-slate-900">{g.label}</Td>
                      <Td right mono>{nf(g.units)}</Td>
                      <Td right mono>
                        <div className="flex items-center justify-end gap-2">
                          <span className={g.attach >= 0.35 ? 'font-bold text-emerald-700' : ''}>{pct(g.attach)}</span>
                          <span className="inline-block w-20"><Bar value={g.attach} max={1} tone={g.revenue ? 'emerald' : 'slate'} /></span>
                        </div>
                      </Td>
                      <Td right mono>{g.revenue ? tl(g.unitPrice) : '—'}</Td>
                      <Td right mono>{g.revenue ? tlShort(g.revenue) : '—'}</Td>
                      <Td right mono className="text-slate-400">{g.revenue ? pct(g.revenueShare) : '—'}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-3 border-l-4 border-emerald-500 bg-emerald-50 px-3 py-2 text-[13px] leading-relaxed text-emerald-900">
              Rezervasyonların {pct(extraGroups.find((g) => g.key === 'koruma_ucretsiz')?.attach || 0)}'i ücretsiz korumada kalıyor.
              Ek sürücü {tl(extraGroups.find((g) => g.key === 'ek_surucu')?.unitPrice || 0)}'ye satılıyor.
              Yükseltme fırsatı burada — ama hangi adımda vazgeçildiği ölçülmediği için bugün optimize edilemez.
            </p>
          </Section>

          {/* ---------------------------------------------------------- pazar */}
          <Section
            icon={Globe} eyebrow="Pazarlar" title="Yurt dışı talep — en yüksek sepetli, en az konuşulan segment"
            note={`Yurt dışı oturumun ${pct(d.abroad.sessionShare)}'i ama cironun ${pct(d.abroad.revenueShare)}'i. Ortalama sepet ${tl(d.abroad.aov)} — yurt içinin katı.`}
          >
            <div className="grid gap-4 lg:grid-cols-2">
              <div className="overflow-hidden border border-slate-200 bg-white">
                <table className="w-full">
                  <thead><tr><Th>Ülke</Th><Th right>Oturum</Th><Th right>Rez.</Th><Th right>CVR</Th><Th right>Sepet</Th></tr></thead>
                  <tbody>
                    {countries.map((c) => (
                      <tr key={c.name} className="hover:bg-slate-50">
                        <Td className="font-medium text-slate-900">{c.name}</Td>
                        <Td right mono>
                          <div>{nf(c.sessions)}</div>
                          <div className="mt-1"><Bar value={c.sessions} max={maxCountry} tone={c.transactions ? 'emerald' : 'rose'} /></div>
                        </Td>
                        <Td right mono>{nf(c.transactions)}</Td>
                        <Td right mono className={c.transactions === 0 ? 'font-bold text-rose-600' : ''}>{pct(c.cvr, 2)}</Td>
                        <Td right mono className={c.aov > s.aov * 1.5 ? 'font-bold text-emerald-700' : ''}>{c.transactions ? tl(c.aov) : '—'}</Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="overflow-hidden border border-slate-200 bg-white">
                <div className="border-b border-slate-200 px-3 py-2 text-[11px] font-bold uppercase tracking-wider text-slate-500">Dil</div>
                <table className="w-full">
                  <thead><tr><Th>Tarayıcı dili</Th><Th right>Oturum</Th><Th right>Rez.</Th><Th right>Ciro</Th></tr></thead>
                  <tbody>
                    {(d.languages || []).slice(0, 12).map((l) => (
                      <tr key={l.name} className="hover:bg-slate-50">
                        <Td className="font-medium text-slate-900">{l.name}</Td>
                        <Td right mono>{nf(l.sessions)}</Td>
                        <Td right mono>{nf(l.transactions)}</Td>
                        <Td right mono>{tlShort(l.revenue)}</Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </Section>

          {/* --------------------------------------------------------- kanal */}
          <Section
            icon={TrendingUp} eyebrow="Kaynak" title="Kanal, kampanya ve boşa yanan trafik"
          >
            {d.wasted?.length ? (
              <div className="mb-4 border-l-4 border-rose-500 bg-rose-50 px-4 py-3">
                <div className="flex items-start gap-2">
                  <Ban size={16} className="mt-0.5 shrink-0 text-rose-600" />
                  <div className="text-[13px] leading-relaxed text-rose-900">
                    <strong>{nf(d.wastedSessions)} oturum, sıfır rezervasyon.</strong>{' '}
                    {d.wasted.map((w) => `${w.name} (${nf(w.sessions)})`).join(' · ')}.
                    <div className="mt-1">
                      Toplam oturumun {pct(d.wastedSessions / (s.sessions || 1))}'i. Ya envanter tamamen alakasız,
                      ya tıklamalar siteye hiç ulaşmıyor — her iki durumda da bütçe yakıyor.
                    </div>
                  </div>
                </div>
              </div>
            ) : null}

            <div className="grid gap-4 lg:grid-cols-2">
              <div className="overflow-hidden border border-slate-200 bg-white">
                <div className="border-b border-slate-200 px-3 py-2 text-[11px] font-bold uppercase tracking-wider text-slate-500">Kanal</div>
                <table className="w-full">
                  <thead><tr><Th>Kanal</Th><Th right>Oturum</Th><Th right>Rez.</Th><Th right>CVR</Th><Th right>Ciro</Th></tr></thead>
                  <tbody>
                    {channels.filter((c) => c.sessions >= 50).map((c) => (
                      <tr key={c.name} className="hover:bg-slate-50">
                        <Td className="font-medium text-slate-900">{c.name}</Td>
                        <Td right mono>
                          <div>{nf(c.sessions)}</div>
                          <div className="mt-1"><Bar value={c.sessions} max={maxChannel} tone={c.transactions ? 'emerald' : 'rose'} /></div>
                        </Td>
                        <Td right mono>{nf(c.transactions)}</Td>
                        <Td right mono className={c.transactions === 0 ? 'font-bold text-rose-600' : ''}>{pct(c.cvr, 2)}</Td>
                        <Td right mono>{tlShort(c.revenue)}</Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="overflow-hidden border border-slate-200 bg-white">
                <div className="border-b border-slate-200 px-3 py-2 text-[11px] font-bold uppercase tracking-wider text-slate-500">Kampanya</div>
                <div className="max-h-[420px] overflow-y-auto">
                  <table className="w-full">
                    <thead><tr><Th>Kampanya</Th><Th right>Oturum</Th><Th right>Rez.</Th><Th right>CVR</Th><Th right>Ciro</Th></tr></thead>
                    <tbody>
                      {campaigns.map((c) => (
                        <tr key={c.name} className="hover:bg-slate-50">
                          <Td className="text-[12px] font-medium text-slate-900">{c.name}</Td>
                          <Td right mono>{nf(c.sessions)}</Td>
                          <Td right mono>{nf(c.transactions)}</Td>
                          <Td right mono className={c.sessions >= 1000 && c.cvr < 0.002 ? 'font-bold text-rose-600' : ''}>{pct(c.cvr, 2)}</Td>
                          <Td right mono>{tlShort(c.revenue)}</Td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="mt-4 grid gap-4 lg:grid-cols-2">
              <div className="overflow-hidden border border-slate-200 bg-white">
                <div className="border-b border-slate-200 px-3 py-2 text-[11px] font-bold uppercase tracking-wider text-slate-500">Cihaz</div>
                <table className="w-full">
                  <thead><tr><Th>Cihaz</Th><Th right>Oturum</Th><Th right>Rez.</Th><Th right>CVR</Th><Th right>Sepet</Th></tr></thead>
                  <tbody>
                    {(d.devices || []).filter((x) => x.sessions >= 50).map((x) => (
                      <tr key={x.name}>
                        <Td className="font-medium text-slate-900">{x.name}</Td>
                        <Td right mono>{nf(x.sessions)}</Td>
                        <Td right mono>{nf(x.transactions)}</Td>
                        <Td right mono>{pct(x.cvr, 2)}</Td>
                        <Td right mono>{tl(x.aov)}</Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="overflow-hidden border border-slate-200 bg-white">
                <div className="border-b border-slate-200 px-3 py-2 text-[11px] font-bold uppercase tracking-wider text-slate-500">Yeni · geri dönen</div>
                <table className="w-full">
                  <thead><tr><Th>Segment</Th><Th right>Oturum</Th><Th right>Rez.</Th><Th right>CVR</Th><Th right>Ciro</Th></tr></thead>
                  <tbody>
                    {(d.newReturning || []).filter((x) => x.name === 'new' || x.name === 'returning').map((x) => (
                      <tr key={x.name}>
                        <Td className="font-medium text-slate-900">{x.name === 'new' ? 'Yeni' : 'Geri dönen'}</Td>
                        <Td right mono>{nf(x.sessions)}</Td>
                        <Td right mono>{nf(x.transactions)}</Td>
                        <Td right mono className={x.name === 'returning' ? 'font-bold text-emerald-700' : ''}>{pct(x.cvr, 2)}</Td>
                        <Td right mono>{tlShort(x.revenue)}</Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="px-3 py-2 text-[12px] leading-relaxed text-slate-500">
                  Geri dönen kullanıcı çok daha iyi dönüşüyor — ama yeni kullanıcı oranı {pct(s.newShare)} olduğu için
                  bu segment olduğundan küçük görünüyor. <code>login</code> event'i olmadan gerçek boyutu bilinemez.
                </p>
              </div>
            </div>
          </Section>

          {/* --------------------------------------------------------- zaman */}
          <Section icon={Clock} eyebrow="Zaman" title="Rezervasyon ne zaman yapılıyor"
            note="Bu, rezervasyonun YAPILDIĞI zaman. Aracın ALINACAĞI tarih ölçülmüyor — pickup_date parametresi yok, bu yüzden sezon ve doluluk analizi bugün mümkün değil.">
            <div className="grid gap-4 lg:grid-cols-2">
              <div className="border border-slate-200 bg-white p-4">
                <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Haftanın günü</div>
                <div className="mt-3 space-y-2">
                  {(d.dow || []).map((x) => (
                    <div key={x.name} className="flex items-center gap-3">
                      <span className="w-24 shrink-0 text-[13px] text-slate-600">{x.name}</span>
                      <span className="flex-1"><Bar value={x.transactions} max={maxDowTx} /></span>
                      <span className="w-12 shrink-0 text-right text-[13px] tabular-nums text-slate-700">{nf(x.transactions)}</span>
                    </div>
                  ))}
                </div>
                <p className="mt-3 text-[12px] leading-relaxed text-slate-500">
                  Hafta içi hafta sonundan belirgin yüksek — planlı ve kurumsal rezervasyon davranışı.
                  <code> customer_type</code> parametresi olmadığı için bireysel/kurumsal ayrımı doğrulanamıyor.
                </p>
              </div>
              <div className="border border-slate-200 bg-white p-4">
                <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Saat</div>
                <div className="mt-3 flex h-[160px] items-end gap-[3px]">
                  {(d.hours || []).map((x) => (
                    <div key={x.hour} className="flex flex-1 flex-col items-center gap-1">
                      <div className="w-full bg-emerald-400" style={{ height: `${(x.transactions / maxHourTx) * 130}px` }} title={`${x.hour}:00 · ${nf(x.transactions)} rezervasyon`} />
                      <span className="text-[9px] text-slate-400">{x.hour}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Section>

          {/* --------------------------------------------------------- trend */}
          <Section icon={TrendingUp} eyebrow="Trend" title="Aylık ciro ve ortalama kiralama süresi"
            note="Aralık 2025 sütunu gerçek bir düşüş değil, ölçüm boşluğudur — o ay purchase event'i 8 Aralık'tan sonra hiç gelmedi. Ortalama kiralama süresi çizgisi Ocak 2026'da başlar; rental_days özel metriği o tarihte devreye girdi.">
            <MonthlyChart rows={d.monthly} />
            <div className="mt-3 overflow-x-auto border border-slate-200 bg-white">
              <table className="w-full min-w-[720px]">
                <thead><tr><Th>Ay</Th><Th right>Oturum</Th><Th right>Rez.</Th><Th right>Ciro</Th><Th right>Sepet</Th><Th right>Ort. gün</Th><Th right>Sayfa/oturum</Th></tr></thead>
                <tbody>
                  {[...d.monthly].reverse().map((m) => (
                    <tr key={m.ym} className={m.ym === '202512' ? 'bg-rose-50' : 'hover:bg-slate-50'}>
                      <Td className="font-medium text-slate-900">{ymLabel(m.ym)}</Td>
                      <Td right mono>{nf(m.sessions)}</Td>
                      <Td right mono>{nf(m.transactions)}</Td>
                      <Td right mono>{tlShort(m.revenue)}</Td>
                      <Td right mono>{m.transactions ? tl(m.aov) : '—'}</Td>
                      <Td right mono>{m.avgDays != null ? nf1(m.avgDays) : '—'}</Td>
                      <Td right mono className={m.pvPerSession < 2 ? 'text-rose-600' : 'text-slate-500'}>{nf1(m.pvPerSession)}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-2 text-[12px] leading-relaxed text-slate-500">
              Sayfa/oturum sütunu Aralık 2025'te 2,4'ten 1,4'e düşüyor ve orada kalıyor. Bu, yeniden tag'lemenin
              bıraktığı oturum parçalanmasıdır — SPA rota değişimleri sayfa görüntüleme üretmiyor.
            </p>
          </Section>

          {/* -------------------------------------------------------- eksikler */}
          <Section
            icon={AlertTriangle} eyebrow="Eksikler" title={`Tanımlanması gereken ${missing.length} event`}
            note="Sıra huni sırasıdır. 'Ölü' olanlar bir kez çalışıp durdu — yeniden yayına alınmaları yeni tanım değil, onarım."
          >
            <div className="border border-slate-200 bg-white">
              <table className="w-full">
                <thead><tr><Th>Durum</Th><Th>Event</Th><Th>Ne için</Th></tr></thead>
                <tbody>
                  {missing.map((b) => (
                    <tr key={b.name} className="hover:bg-slate-50">
                      <Td><Badge kind={b.durum}>{b.durum === 'olu' ? `ölü · son ${ymLabel(b.lastMonth)}` : 'yok'}</Badge></Td>
                      <Td><code className="text-[12px] font-semibold text-slate-800">{b.name}</code>
                        <div className="text-[12px] text-slate-500">{b.label}</div>
                      </Td>
                      <Td className="text-[13px] text-slate-600">{b.why}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-4 grid gap-4 lg:grid-cols-3">
              <div className="border border-slate-200 bg-white p-4">
                <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                  Önerilen event kapsamlı boyutlar
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {(h.onerilen?.event || []).map((x) => (
                    <span key={x} className="border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[12px] text-slate-700">{x}</span>
                  ))}
                </div>
                <p className="mt-2 text-[12px] text-slate-500">
                  {h.customDimensionSlots?.limit - (h.customDimensionSlots?.used || 0)} slot boşta.
                </p>
              </div>
              <div className="border border-slate-200 bg-white p-4">
                <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                  Önerilen item kapsamlı boyutlar
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {(h.onerilen?.item || []).map((x) => (
                    <span key={x} className="border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[12px] text-slate-700">{x}</span>
                  ))}
                </div>
                <p className="mt-2 text-[12px] text-slate-500">
                  {h.itemDimensionSlots?.limit} slotun tamamı boş. Araç grubu analizi event slotu harcamadan yapılabilir.
                </p>
              </div>
              <div className="border border-slate-200 bg-white p-4">
                <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Önerilen özel metrikler</div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {(h.onerilen?.metric || []).map((x) => (
                    <span key={x} className="border border-teal-200 bg-teal-50 px-1.5 py-0.5 text-[12px] text-teal-700">{x}</span>
                  ))}
                </div>
                <p className="mt-2 text-[12px] text-slate-500">Mevcut <code>rental_days</code> korunur.</p>
              </div>
            </div>

            <div className="mt-4 border-l-4 border-slate-800 bg-slate-50 px-4 py-3 text-[13px] leading-relaxed text-slate-700">
              <strong>Özet:</strong> mevcut kurulum rezervasyonu kaydediyor ama rezervasyona giden yolu kaydetmiyor.
              Kim aradı, ne aradı, araç bulabildi mi, nerede vazgeçti — hiçbiri ölçülmüyor. Seçili aralıktaki{' '}
              {tlShort(s.revenue)} ciro verisi sağlam ve zengin; sorun bu cironun <em>nasıl</em> oluştuğunu
              açıklayamıyor olmamız. İlk adım hiç kod gerektirmiyor: veri saklamayı 14 aya çıkarmak,
              BigQuery export'u açmak ve gelişmiş ölçümü etkinleştirmek.
            </div>
          </Section>

          {/* ------------------------------------------ rezervasyon anatomisi */}
          {d.order ? (
            <Section
              icon={Receipt} eyebrow="Rezervasyon anatomisi"
              title="Bir rezervasyon geldiğinde elimizde tam olarak ne var"
              note="Aşağıdaki her satırın durumu canlı GA4'ten ölçüldü — doluluk oranı gerçek purchase event'lerinden hesaplandı, kayıtlı olmayan alanlar ise GA4 Admin API'sindeki özel boyut listesinden doğrulandı. 'Sitede' sütunu 17 Ağustos 2026 tarihli canlı site denetiminden gelir ve asıl mesaj odur: eksik alanların neredeyse tamamı toplanamıyor değil, toplanıyor ama GA4'e verilmiyor."
            >
              {/* durum şeridi */}
              <div className="grid grid-cols-2 gap-px bg-slate-200 md:grid-cols-4">
                <Kpi label="Elimizde var" value={nf(d.order.counts.tam)} sub="alan dolu geliyor" tone="emerald" />
                <Kpi label="Kısmi" value={nf(d.order.counts.kismi)} sub="bazı rezervasyonlarda dolu" tone="amber" />
                <Kpi label="Alan var, hep boş" value={nf(d.order.counts.bos)} sub="GA4 alanı mevcut, değer gelmiyor" />
                <Kpi label="GA4'te hiç yok" value={nf(d.order.counts.kayitli_degil)} sub="özel boyut kaydı bile yapılmamış" tone="rose" />
              </div>

              {/* örnek fiş */}
              <div className="mt-5">
                <p className="mb-2 text-[13px] text-slate-500">
                  Aşağıdaki gerçek bir rezervasyon. Solda GA4'ün bize söyledikleri, sağda aynı rezervasyon
                  hakkında hiçbir fikrimizin olmadığı alanlar.
                </p>
                <OrderReceipt sample={d.order.sample} fields={d.order.fields} />
              </div>

              {/* kupon bulgusu */}
              {d.order.coupon && d.order.coupon.withCoupon ? (
                <div className="mt-5 border-l-4 border-emerald-500 bg-emerald-50 px-4 py-3">
                  <div className="text-[13px] leading-relaxed text-emerald-900">
                    <strong>İyi haber:</strong> kampanya kodu ölçülüyor.{' '}
                    Rezervasyonların <strong>{pct(d.order.coupon.share)}</strong>'i ({nf(d.order.coupon.withCoupon)} rezervasyon)
                    bir kupon koduyla yapılmış ve GA4 bunu <code>orderCoupon</code> alanında taşıyor —
                    {' '}{nf(d.order.coupon.distinctCodes)} farklı kod. Yani kod bazında kampanya verimliliği
                    bugün, ek geliştirme olmadan ölçülebilir.
                    <div className="mt-1.5">
                      Kuponlu ortalama sepet <strong>{tl(d.order.coupon.aovWith)}</strong>, kuponsuz{' '}
                      <strong>{tl(d.order.coupon.aovWithout)}</strong>.
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {d.order.coupon.top.map((c) => (
                      <span key={c.code} className="border border-emerald-200 bg-white px-2 py-0.5 text-[12px] text-emerald-800">
                        {c.code} <span className="tabular-nums text-emerald-600">· {nf(c.transactions)}</span>
                      </span>
                    ))}
                  </div>
                </div>
              ) : null}

              {/* tam alan tablosu */}
              <div className="mt-5 flex flex-wrap items-center gap-4 text-[12px] text-slate-500">
                {Object.entries(DURUM).filter(([k]) => k !== 'bilinmiyor').map(([k, v]) => (
                  <span key={k} className="flex items-center gap-1.5">
                    <span className={`inline-block h-2.5 w-2.5 ${v.nokta}`} />
                    <span className={v.renk}>{v.etiket}</span>
                  </span>
                ))}
              </div>

              <div className="mt-2 overflow-x-auto border border-slate-200 bg-white">
                <table className="w-full min-w-[940px]">
                  <thead>
                    <tr>
                      <Th>Durum</Th><Th>Alan</Th><Th right>Doluluk</Th>
                      <Th>Örnek değer</Th><Th>Sitede</Th><Th>Not</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {['Kimlik ve tutar', 'Kiralama', 'Araç', 'Fiyatlandırma', 'Müşteri', 'Pazarlama bağlamı'].map((grup) => {
                      const satirlar = d.order.fields.filter((f) => f.group === grup);
                      if (!satirlar.length) return null;
                      return (
                        <React.Fragment key={grup}>
                          <tr>
                            <td colSpan={6} className="border-b border-slate-200 bg-slate-50 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-500">
                              {grup}
                            </td>
                          </tr>
                          {satirlar.map((f) => {
                            const st = DURUM[f.durum] || DURUM.bilinmiyor;
                            return (
                              <tr key={f.key} className={st.satir}>
                                <Td>
                                  <span className="flex items-center gap-1.5">
                                    <span className={`inline-block h-2.5 w-2.5 shrink-0 ${st.nokta}`} />
                                    <span className={`text-[11px] font-bold uppercase tracking-wider ${st.renk}`}>{st.etiket}</span>
                                  </span>
                                </Td>
                                <Td>
                                  <div className="font-medium text-slate-900">{f.label}</div>
                                  <code className="text-[11px] text-slate-400">{f.key}</code>
                                </Td>
                                <Td right mono className={f.durum === 'tam' ? 'font-semibold text-emerald-700' : 'text-slate-500'}>
                                  {f.rate == null ? '—' : pct(f.rate, 0)}
                                </Td>
                                <Td className="text-[12px] text-slate-500">
                                  {(f.samples && f.samples.length)
                                    ? f.samples.map((s) => <div key={s} className="truncate max-w-[230px]">{s}</div>)
                                    : (f.durum === 'tam' ? <span className="text-slate-400">geliyor</span> : '—')}
                                </Td>
                                <Td>
                                  {f.site === 'var'
                                    ? <Badge kind={f.durum === 'tam' ? 'var' : 'uyari'}>sitede var</Badge>
                                    : f.site === 'turetilebilir'
                                      ? <Badge kind="uyari">türetilebilir</Badge>
                                      : <span className="text-slate-400">—</span>}
                                </Td>
                                <Td className="text-[12px] leading-snug text-slate-500">{f.note || ''}</Td>
                              </tr>
                            );
                          })}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="mt-4 border-l-4 border-slate-800 bg-slate-50 px-4 py-3 text-[13px] leading-relaxed text-slate-700">
                <strong>Okunuşu:</strong> rezervasyonun <em>ticari</em> tarafını biliyoruz — kim, nereden,
                hangi araçla, kaç güne, ne kadara. Bilmediğimiz şey rezervasyonun <em>operasyonel ve
                ticari kararlara dönüşen</em> tarafı: aracın hangi tarihte alınacağı, kaç gün önceden
                rezerve edildiği, hangi ödeme planının seçildiği, müşterinin bireysel mi kurumsal mı olduğu.
                Bu alanların hemen hepsi sitede zaten toplanıyor — biri hariç hiçbiri yeni veri toplama
                gerektirmiyor, sadece <code>dataLayer</code>'a yazılmayı bekliyor.
              </div>
            </Section>
          ) : null}

          {d.meta ? (
            <p className="mt-10 border-t border-slate-200 pt-3 text-[11px] text-slate-400">
              Canlı GA4 Data API · property {d.meta.propertyId} ({d.meta.propertyName}) · {d.meta.currency} ·{' '}
              {d.meta.timeZone} · {d.meta.serviceLevel === 'GOOGLE_ANALYTICS_STANDARD' ? 'Standart sürüm' : d.meta.serviceLevel} ·{' '}
              {new Date(d.meta.generatedAt).toLocaleString('tr-TR')} itibarıyla{d.meta.cached ? ' (önbellek)' : ` (${(d.meta.elapsedMs / 1000).toFixed(1)} sn)`}
            </p>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
